import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  Search, X, Users, ChefHat, BookOpen, TrendingUp, Clock, 
  MapPin, Flame, Star, Heart, Filter, ArrowRight, Hash
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import debounce from 'lodash.debounce';

const POPULAR_SEARCHES = [
  'couscous', 'brik', 'lablabi', 'ojja', 'makroudh', 'chorba', 'tajine', 'harissa'
];

const REGIONS = [
  { id: 'tunis', name: 'Tunis' },
  { id: 'sfax', name: 'Sfax' },
  { id: 'sousse', name: 'Sousse' },
  { id: 'kairouan', name: 'Kairouan' },
  { id: 'bizerte', name: 'Bizerte' },
];

export default function SearchPage() {
  const { api } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    users: [],
    recipes: [],
    posts: [],
    challenges: []
  });
  const [recentSearches, setRecentSearches] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('mharher_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  }, []);

  // Search when query changes
  useEffect(() => {
    if (query.length >= 2) {
      debouncedSearch(query);
    } else {
      setResults({ users: [], recipes: [], posts: [], challenges: [] });
    }
  }, [query, activeTab]);

  // Update URL params
  useEffect(() => {
    if (query) {
      setSearchParams({ q: query, tab: activeTab });
    }
  }, [query, activeTab]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) return;
    
    setLoading(true);
    try {
      // Use unified search endpoint
      const response = await api.get(`/search?q=${encodeURIComponent(searchQuery)}&limit=20`);
      
      setResults({
        users: response.data?.users || [],
        recipes: response.data?.recipes || [],
        posts: [],
        challenges: response.data?.challenges || []
      });

      // Save to recent searches
      saveRecentSearch(searchQuery);
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to empty results on error
      setResults({ users: [], recipes: [], posts: [], challenges: [] });
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((q) => performSearch(q), 300),
    []
  );

  const saveRecentSearch = (searchQuery) => {
    const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('mharher_recent_searches', JSON.stringify(updated));
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('mharher_recent_searches');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      performSearch(query.trim());
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    performSearch(suggestion);
    setShowSuggestions(false);
  };

  const totalResults = results.users.length + results.recipes.length + results.posts.length;

  return (
    <div className="min-h-screen bg-[#F8F5F2]" data-testid="search-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder={t('search.placeholder')}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              className="w-full pl-12 pr-12 py-3 text-base rounded-xl border-gray-200 focus:border-[#1E4ED8] focus:ring-[#1E4ED8]"
              autoFocus
              data-testid="search-input"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setResults({ users: [], recipes: [], posts: [], challenges: [] });
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </form>

          {/* Suggestions dropdown */}
          <AnimatePresence>
            {showSuggestions && !query && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute left-0 right-0 mx-4 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden"
              >
                {/* Recent searches */}
                {recentSearches.length > 0 && (
                  <div className="p-3 border-b">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500 uppercase">{t('search.recent', 'Recherches récentes')}</span>
                      <button 
                        onClick={clearRecentSearches}
                        className="text-xs text-[#E07A5F] hover:underline"
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map((search, i) => (
                        <button
                          key={i}
                          onClick={() => handleSuggestionClick(search)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-sm transition-colors"
                        >
                          <Clock className="w-3 h-3 text-gray-400" />
                          {search}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Popular searches */}
                <div className="p-3">
                  <span className="text-xs font-medium text-gray-500 uppercase mb-2 block">{t('search.popular')}</span>
                  <div className="flex flex-wrap gap-2">
                    {POPULAR_SEARCHES.map((search, i) => (
                      <button
                        key={i}
                        onClick={() => handleSuggestionClick(search)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#1E4ED8]/5 hover:bg-[#1E4ED8]/10 text-[#1E4ED8] rounded-full text-sm transition-colors"
                      >
                        <TrendingUp className="w-3 h-3" />
                        {search}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Browse by region */}
                <div className="p-3 border-t bg-gray-50">
                  <span className="text-xs font-medium text-gray-500 uppercase mb-2 block">{t('recipes.region', 'Explorer par région')}</span>
                  <div className="flex flex-wrap gap-2">
                    {REGIONS.map((region) => (
                      <Link
                        key={region.id}
                        to={`/regions/${region.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 hover:border-[#1E4ED8] rounded-full text-sm transition-colors"
                      >
                        <MapPin className="w-3 h-3 text-[#E07A5F]" />
                        {region.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {query.length >= 2 ? (
          <>
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="w-full bg-white rounded-xl p-1 shadow-sm">
                <TabsTrigger 
                  value="all" 
                  className="flex-1 rounded-lg text-sm data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white"
                >
                  {t('common.all')} ({totalResults})
                </TabsTrigger>
                <TabsTrigger 
                  value="users" 
                  className="flex-1 rounded-lg text-sm data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white"
                >
                  <Users className="w-4 h-4 mr-1" />
                  {t('search.users')} ({results.users.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="recipes" 
                  className="flex-1 rounded-lg text-sm data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white"
                >
                  <ChefHat className="w-4 h-4 mr-1" />
                  {t('search.recipes')} ({results.recipes.length})
                </TabsTrigger>
              </TabsList>

              {/* Loading state */}
              {loading ? (
                <div className="space-y-4 mt-6">
                  {[1, 2, 3, 4].map((i) => (
                    <Card key={i} className="p-4 rounded-xl">
                      <div className="flex items-center gap-4">
                        <Skeleton className="w-12 h-12 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-5 w-1/3" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <>
                  {/* All results */}
                  <TabsContent value="all" className="mt-6 space-y-6">
                    {totalResults === 0 ? (
                      <EmptyResults query={query} t={t} />
                    ) : (
                      <>
                        {/* Users section */}
                        {results.users.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                <Users className="w-4 h-4 text-[#1E4ED8]" />
                                {t('search.users')}
                              </h3>
                              {results.users.length > 3 && (
                                <button 
                                  onClick={() => setActiveTab('users')}
                                  className="text-sm text-[#1E4ED8] hover:underline"
                                >
                                  {t('common.seeAll')} →
                                </button>
                              )}
                            </div>
                            <div className="grid gap-3">
                              {results.users.slice(0, 3).map((user) => (
                                <UserCard key={user.id} user={user} />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recipes section */}
                        {results.recipes.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                <ChefHat className="w-4 h-4 text-[#E07A5F]" />
                                {t('search.recipes')}
                              </h3>
                              {results.recipes.length > 3 && (
                                <button 
                                  onClick={() => setActiveTab('recipes')}
                                  className="text-sm text-[#1E4ED8] hover:underline"
                                >
                                  {t('common.seeAll')} →
                                </button>
                              )}
                            </div>
                            <div className="grid gap-3">
                              {results.recipes.slice(0, 3).map((recipe) => (
                                <RecipeCard key={recipe.id} recipe={recipe} />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </TabsContent>

                  {/* Users only */}
                  <TabsContent value="users" className="mt-6">
                    {results.users.length === 0 ? (
                      <EmptyResults query={query} type={t('search.users').toLowerCase()} t={t} />
                    ) : (
                      <div className="grid gap-3">
                        {results.users.map((user) => (
                          <UserCard key={user.id} user={user} />
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Recipes only */}
                  <TabsContent value="recipes" className="mt-6">
                    {results.recipes.length === 0 ? (
                      <EmptyResults query={query} type={t('search.recipes').toLowerCase()} t={t} />
                    ) : (
                      <div className="grid gap-3">
                        {results.recipes.map((recipe) => (
                          <RecipeCard key={recipe.id} recipe={recipe} t={t} />
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </>
              )}
            </Tabs>
          </>
        ) : (
          /* Empty state / Discovery */
          <div className="space-y-8">
            {/* Trending hashtags */}
            <div>
              <h3 className="font-fraunces text-lg font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#E07A5F]" />
                {t('search.trending')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {['#CouscousFriday', '#CuisineTunisienne', '#RecetteFamille', '#StreetFood', '#Ramadan2026'].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => handleSuggestionClick(tag.replace('#', ''))}
                    className="px-4 py-2 bg-white rounded-full text-sm font-medium hover:bg-[#1E4ED8]/5 hover:text-[#1E4ED8] transition-colors shadow-sm"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Discover chefs */}
            <div>
              <h3 className="font-fraunces text-lg font-bold mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-[#FFD700]" />
                {t('home.discoverChefs', 'Chefs à découvrir')}
              </h3>
              <DiscoverChefs />
            </div>

            {/* Browse categories */}
            <div>
              <h3 className="font-fraunces text-lg font-bold mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#1E4ED8]" />
                {t('recipes.title', 'Catégories')}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { name: 'Plats principaux', icon: '🍲', count: 156 },
                  { name: 'Entrées', icon: '🥗', count: 89 },
                  { name: 'Pâtisseries', icon: '🍰', count: 124 },
                  { name: 'Boissons', icon: '🥤', count: 45 },
                  { name: 'Sauces', icon: '🫕', count: 67 },
                  { name: 'Street food', icon: '🌯', count: 78 },
                ].map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleSuggestionClick(cat.name)}
                    className="flex items-center gap-3 p-4 bg-white rounded-xl hover:shadow-md transition-all text-left"
                  >
                    <span className="text-2xl">{cat.icon}</span>
                    <div>
                      <p className="font-medium text-gray-900">{cat.name}</p>
                      <p className="text-xs text-gray-500">{cat.count} recettes</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// User Card Component
function UserCard({ user }) {
  const navigate = useNavigate();
  
  return (
    <Card 
      className="p-4 rounded-xl hover:shadow-md transition-all cursor-pointer"
      onClick={() => navigate(`/u/${user.username}`)}
    >
      <div className="flex items-center gap-4">
        <Avatar className="w-14 h-14 border-2 border-white shadow">
          <AvatarImage src={user.profile_picture} />
          <AvatarFallback className="bg-[#1E4ED8] text-white text-lg">
            {user.display_name?.[0] || user.username?.[0]}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-gray-900 truncate">
              {user.display_name || user.username}
            </h4>
            {user.is_verified && (
              <div className="w-4 h-4 rounded-full bg-[#1E4ED8] flex items-center justify-center flex-shrink-0">
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500">@{user.username}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Flame className="w-3 h-3 text-[#E07A5F]" />
              {user.barka_points || user.barka || 0} BARKA
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {user.followers_count || 0} followers
            </span>
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-gray-300" />
      </div>
    </Card>
  );
}

// Recipe Card Component
function RecipeCard({ recipe, t }) {
  const navigate = useNavigate();
  
  return (
    <Card 
      className="p-4 rounded-xl hover:shadow-md transition-all cursor-pointer"
      onClick={() => navigate(`/recipe/${recipe.id}`)}
    >
      <div className="flex gap-4">
        <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
          {recipe.cover_image || recipe.images?.[0] ? (
            <img 
              src={recipe.cover_image || recipe.images?.[0]} 
              alt={recipe.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ChefHat className="w-8 h-8 text-gray-300" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 truncate">{recipe.title}</h4>
          <p className="text-sm text-gray-500 line-clamp-1">{recipe.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            {recipe.difficulty && (
              <Badge variant="outline" className="text-xs">
                {recipe.difficulty === 'easy' ? t('recipes.easy') : recipe.difficulty === 'medium' ? t('recipes.medium') : t('recipes.hard')}
              </Badge>
            )}
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3 text-[#E07A5F]" />
              {recipe.likes_count || 0}
            </span>
            {recipe.prep_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {recipe.prep_time}min
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

// Empty Results Component
function EmptyResults({ query, type, t }) {
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <Search className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">
        {t('search.noResults')} "{query}"
      </h3>
      <p className="text-gray-500 text-sm">
        {type 
          ? `${t('common.noResults', 'Aucun résultat trouvé')}` 
          : t('common.tryAgain')
        }
      </p>
    </div>
  );
}

// Discover Chefs Component
function DiscoverChefs() {
  const { api } = useAuth();
  const [chefs, setChefs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChefs = async () => {
      try {
        const response = await api.get('/leaderboard?limit=5');
        setChefs(response.data?.leaderboard || []);
      } catch (error) {
        console.error('Error fetching chefs:', error);
      }
    };
    fetchChefs();
  }, []);

  if (chefs.length === 0) return null;

  return (
    <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
      {chefs.map((chef) => (
        <div 
          key={chef.id}
          onClick={() => navigate(`/u/${chef.username}`)}
          className="flex-shrink-0 w-32 text-center cursor-pointer group"
        >
          <Avatar className="w-20 h-20 mx-auto border-2 border-white shadow-lg group-hover:scale-105 transition-transform">
            <AvatarImage src={chef.profile_picture} />
            <AvatarFallback className="bg-gradient-to-br from-[#1E4ED8] to-[#E07A5F] text-white text-xl">
              {chef.username?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <p className="font-medium text-sm mt-2 truncate">{chef.display_name || chef.username}</p>
          <p className="text-xs text-[#E07A5F]">{chef.barka_points || 0} BARKA</p>
        </div>
      ))}
    </div>
  );
}
